import { ref, computed } from 'vue';
import type { SpotifyUser } from '@/types/spotify';

const SPOTIFY_AUTH_URL = 'https://accounts.spotify.com/authorize';
const SPOTIFY_TOKEN_URL = 'https://accounts.spotify.com/api/token';
const SPOTIFY_API_BASE = 'https://api.spotify.com/v1';

// Keys für localStorage
const LS_ACCESS_TOKEN = 'spotify_access_token';
const LS_REFRESH_TOKEN = 'spotify_refresh_token';
const LS_EXPIRES_AT = 'spotify_expires_at';
const LS_AUTH_STATE = 'spotify_auth_state';
const LS_CODE_VERIFIER = 'spotify_code_verifier';

// Globaler State (Singleton)
const accessToken = ref<string | null>(localStorage.getItem(LS_ACCESS_TOKEN));
const refreshToken = ref<string | null>(localStorage.getItem(LS_REFRESH_TOKEN));
const expiresAt = ref<number | null>(
  localStorage.getItem(LS_EXPIRES_AT) ? Number(localStorage.getItem(LS_EXPIRES_AT)) : null
);
const userProfile = ref<SpotifyUser | null>(null);

/**
 * Helper: PKCE Code Verifier / Challenge
 */
function generateRandomString(length: number): string {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return Array.from(array)
    .map((x) => possible[x % possible.length])
    .join('');
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest('SHA-256', data);
}

function base64urlEncode(input: ArrayBuffer): string {
  const bytes = new Uint8Array(input);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

async function createCodeChallenge(verifier: string): Promise<string> {
  const hashed = await sha256(verifier);
  return base64urlEncode(hashed);
}

export function useSpotifyAuth() {
  const clientId = process.env.VUE_APP_SPOTIFY_CLIENT_ID;
  const redirectUri = process.env.VUE_APP_SPOTIFY_REDIRECT_URI;

  const isPremium = computed(() => userProfile.value?.product === 'premium');
  const isAuthenticated = computed(() => !!accessToken.value);

  /**
   * Startet den Spotify OAuth Flow (Authorization Code + PKCE)
   */
  async function login() {
    if (!clientId || !redirectUri) {
      console.error('Spotify Client ID oder Redirect URI fehlt!');
      return;
    }

    // PKCE: Code Verifier & Challenge erstellen
    const codeVerifier = generateRandomString(64);
    const codeChallenge = await createCodeChallenge(codeVerifier);

    // State für CSRF-Schutz
    const state = generateRandomString(16);

    // Im localStorage speichern (für Callback)
    localStorage.setItem(LS_CODE_VERIFIER, codeVerifier);
    localStorage.setItem(LS_AUTH_STATE, state);

    const scopes = [
      'streaming',
      'user-read-email',
      'user-read-private',
      'user-read-playback-state',
      'user-modify-playback-state'
    ];

    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      redirect_uri: redirectUri,
      state,
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      scope: scopes.join(' '),
      show_dialog: 'false'
    });

    // Browser zu Spotify weiterleiten
    window.location.href = `${SPOTIFY_AUTH_URL}?${params.toString()}`;
  }

  /**
   * Verarbeitet den OAuth-Callback (Authorization Code → Access Token)
   */
  async function handleCallback(): Promise<boolean> {
    if (!clientId || !redirectUri) {
      console.error('Spotify Client ID oder Redirect URI fehlt!');
      return false;
    }

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    const storedState = localStorage.getItem(LS_AUTH_STATE);
    const codeVerifier = localStorage.getItem(LS_CODE_VERIFIER);

    if (error) {
      console.error('Spotify OAuth Error:', error);
      return false;
    }

    if (!code) {
      console.error('Kein Authorization Code im Callback vorhanden');
      return false;
    }

    // State prüfen
    if (!state || !storedState || state !== storedState) {
      console.error('State mismatch!');
      return false;
    }

    if (!codeVerifier) {
      console.error('Kein Code Verifier vorhanden (PKCE)');
      return false;
    }

    try {
      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUri,
        client_id: clientId,
        code_verifier: codeVerifier
      });

      const response = await fetch(SPOTIFY_TOKEN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body
      });

      if (!response.ok) {
        console.error('Token-Request fehlgeschlagen:', response.status);
        return false;
      }

      const data = await response.json();

      accessToken.value = data.access_token;
      refreshToken.value = data.refresh_token || null;
      const expiresIn = data.expires_in; // Sekunden
      const expiresAtTs = Date.now() + expiresIn * 1000;

      expiresAt.value = expiresAtTs;

      // Persistieren
      if (accessToken.value) {
        localStorage.setItem(LS_ACCESS_TOKEN, accessToken.value);
      }
      if (refreshToken.value) {
        localStorage.setItem(LS_REFRESH_TOKEN, refreshToken.value);
      }
      localStorage.setItem(LS_EXPIRES_AT, String(expiresAtTs));


      // Einmalige Werte entfernen
      localStorage.removeItem(LS_AUTH_STATE);
      localStorage.removeItem(LS_CODE_VERIFIER);

      // User-Profil laden
      await fetchUserProfile();

      return true;
    } catch (e) {
      console.error('Fehler beim Verarbeiten des Callbacks:', e);
      return false;
    }
  }

  /**
   * Access Token bei Bedarf refreshen (mit Refresh Token)
   */
  async function refreshAccessToken(): Promise<boolean> {
    if (!clientId) {
      console.error('Spotify Client ID fehlt!');
      return false;
    }
    if (!refreshToken.value) {
      console.warn('Kein Refresh Token vorhanden.');
      return false;
    }

    try {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken.value,
        client_id: clientId
      });

      const response = await fetch(SPOTIFY_TOKEN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body
      });

      if (!response.ok) {
        console.error('Refresh-Request fehlgeschlagen:', response.status);
        logout();
        return false;
      }

      const data = await response.json();

      accessToken.value = data.access_token;
      const expiresIn = data.expires_in;
      const expiresAtTs = Date.now() + expiresIn * 1000;
      expiresAt.value = expiresAtTs;

      if (accessToken.value) {
        localStorage.setItem(LS_ACCESS_TOKEN, accessToken.value);
      }
      localStorage.setItem(LS_EXPIRES_AT, String(expiresAtTs));

      // Spotify schickt manchmal ein neues Refresh Token
      if (data.refresh_token) {
        refreshToken.value = data.refresh_token;
      }
      if (refreshToken.value) {
        localStorage.setItem(LS_REFRESH_TOKEN, refreshToken.value);
      }

      return true;
    } catch (e) {
      console.error('Fehler beim Refresh des Tokens:', e);
      logout();
      return false;
    }
  }

  /**
   * Ruft das User-Profil ab
   */
  /**
   * Ruft das User-Profil ab
   */
  async function fetchUserProfile(): Promise<void> {
    if (!accessToken.value) return;

    try {
      const response = await fetch(`${SPOTIFY_API_BASE}/me`, {
        headers: {
          Authorization: `Bearer ${accessToken.value}`
        }
      });

      if (response.ok) {
        userProfile.value = await response.json();
      } else if (response.status === 401) {
        // Token abgelaufen → versuchen zu refreshen
        const ok = await refreshAccessToken();
        if (ok) {
          await fetchUserProfile();
        } else {
          logout();
        }
      } else {
        console.error('Fehler beim Abrufen des User-Profils:', response.status);
      }
    } catch (error) {
      console.error('Fehler beim Abrufen des User-Profils:', error);
    }
  }


  /**
   * Logout
   */
  function logout() {
    accessToken.value = null;
    refreshToken.value = null;
    expiresAt.value = null;
    userProfile.value = null;

    localStorage.removeItem(LS_ACCESS_TOKEN);
    localStorage.removeItem(LS_REFRESH_TOKEN);
    localStorage.removeItem(LS_EXPIRES_AT);
    localStorage.removeItem(LS_AUTH_STATE);
    localStorage.removeItem(LS_CODE_VERIFIER);
  }

  /**
   * Gibt ein gültiges Access Token zurück (refresh bei Bedarf)
   */
  async function getValidAccessToken(): Promise<string | null> {
    if (!accessToken.value) return null;

    // 60 Sekunden Puffer
    if (expiresAt.value && Date.now() > expiresAt.value - 60_000) {
      const ok = await refreshAccessToken();
      if (!ok) return null;
    }

    return accessToken.value;
  }

  /**
   * Synchronous Getter (für Stellen, wo du nicht warten willst)
   * Achtung: refreshed NICHT automatisch!
   */
  function getAccessToken(): string | null {
    return accessToken.value;
  }

  // Beim App-Start: wenn Token vorhanden, Profil nachladen
  if (accessToken.value && !userProfile.value) {
    fetchUserProfile();
  }

  return {
    accessToken: computed(() => accessToken.value),
    userProfile: computed(() => userProfile.value),
    isPremium,
    isAuthenticated,
    login,
    handleCallback,
    logout,
    fetchUserProfile,
    getAccessToken,
    getValidAccessToken
  };
}
